/**
 * Simplified Chinese locale resource.
 *
 * Follows the frozen MBIP V5 terminology glossary (see
 * docs/08_Commercial_BI/12_MBIP_V5_PRESENTATION_I18N_FOUNDATION.md):
 * Sell-in → 一销, Invoiced Sell-in → 开票一销, Sell-out → 二销,
 * Net Channel Flow → 渠道净流量, Customer On Hand → 客户在库,
 * Marketplace Inventory → 平台库存, Pan-EU Stock → 泛欧库存,
 * In Transit → 在途库存, Open PO → 未完成订单, PO Intake → 新增订单,
 * Traffic → 流量, Conversion → 转化率, Missing → 缺失,
 * Unresolved → 未解析, Derived → 推导值, Evidence → 数据依据.
 *
 * Business identities (Model, ASIN, SKU, Invoice Number, PO Number,
 * Customer Code, Country Code, Currency Code, source filenames) are
 * never localized.
 */
export const zhCN: Record<string, string> = {
  // ------------------------------------------------------------------
  // common
  // ------------------------------------------------------------------
  "common.country": "国家",
  "common.model": "型号",
  "common.period": "期间",
  "common.coverage": "数据覆盖",
  "common.evidence": "数据依据",
  "common.authority": "权威来源",
  "common.target": "目标",
  "common.completion": "完成度",
  "common.variance": "差异",
  "common.snapshot": "库存快照",
  "common.status": "状态",
  "common.grain": "粒度",
  "common.view": "视图",
  "common.channel": "渠道",
  "common.businessContext": "业务上下文",
  "common.asOf": "截至 {{value}}",
  "common.periodOf": "期间 {{value}}",
  "common.metricAria": "{{label}} 指标",
  "common.refresh": "刷新",
  "common.retry": "重试",
  "common.readOnly": "只读",
  "common.allCountries": "全部国家",
  "common.allModels": "全部型号",
  "common.allAsins": "全部 ASIN",
  "common.allSourceSkus": "全部源 SKU",
  "common.allExactModels": "全部精确型号",
  "common.allInvoiceCountries": "全部发票国家",
  "common.unresolvedOnly": "仅看未解析",
  "common.businessYear": "业务年度",
  "common.periodGrain": "期间粒度",
  "common.issueDatePeriod": "开票日期期间",
  "common.sortRows": "行排序",
  "common.sortBackendFacts": "后端数据排序",
  "common.approximate": "约",
  "common.missing": "缺失",
  "common.unresolved": "未解析",
  "common.available": "可用",
  "common.rows": "{{count}} 行",
  "common.exactRows": "{{count}} 条精确行",
  "common.exactSourceSkuRows": "{{count}} 条精确源 SKU 行",
  "common.loading": "加载中",
  "common.language": "语言",
  "common.english": "English",
  "common.chinese": "中文",
  "common.workspace": "{{group}} 工作区",
  "common.copiedViewLink": "当前视图链接已复制,包含 URL 中的筛选条件。",
  "common.copyViewLinkFailed": "无法复制当前视图链接。",
  "common.onlinePreview": "在线预览",
  "common.neoUat": "NEO UAT",
  "common.environmentUnverified": "环境未验证",
  "common.onlinePreviewReadOnly": "在线预览 · 只读",
  "common.sourceUatMaintenance": "来源 UAT · 维护中",
  "common.uatReadOnly": "UAT · 只读",
  "common.noSourceTimestamp": "无来源时间戳",
  "common.environmentIdentityUnavailable": "环境身份不可用",
  "common.shareCurrentView": "分享当前视图",
  "common.masterData": "主数据",
  "common.expandNavigation": "展开导航",
  "common.collapseNavigation": "收起导航",
  "common.brandTagline": "商业智能",
  "common.homeAria": "MBIP 首页",
  "common.loadingWorkspace": "正在加载工作区",
  "common.skipToContent": "跳转到主要内容",
  "common.primaryNavigation": "主导航",
  "common.tableControls": "表格控制项",
  "common.tableDisplay": "表格显示",
  "common.columnWidth": "列宽",
  "common.rowHeight": "行高",
  "common.tableDisplayHint": "应用于所有表格；宽表保留横向滚动。",
  "common.compact": "紧凑",
  "common.standard": "标准",
  "common.wide": "宽松",
  "common.comfortable": "舒适",
  "common.reset": "重置",

  // ------------------------------------------------------------------
  // navigation
  // ------------------------------------------------------------------
  "navigation.group.global": "全球",
  "navigation.group.amazon": "亚马逊",
  "navigation.group.joybuy": "京东国际",
  "navigation.group.system": "系统",
  "navigation.actionCenter": "行动中心",
  "navigation.globalSales": "全球销售",
  "navigation.sellOut": "终端销售",
  "navigation.planningInventory": "计划与库存",
  "navigation.cost": "成本",
  "navigation.globalCost": "全球成本",
  "navigation.profitability": "盈利能力",
  "navigation.globalProfitability": "全球盈利能力",
  "navigation.invoiceReconciliation": "发票核对",
  "navigation.sales": "销售",
  "navigation.inventory": "库存",
  "navigation.promotion": "促销",
  "navigation.rebateSettlement": "返利与结算",
  "navigation.operation": "运营",
  "navigation.dataUpload": "数据上传",
  "navigation.orderPlanning": "订单计划",
  "navigation.dataSources": "数据源",
  "navigation.dataSourcesIdentity": "数据源与身份",
  "navigation.masterData": "主数据",
  "navigation.backendConnectivity": "后端连接",
  "navigation.dataImportCenter": "数据导入中心",
  "navigation.amazonSales": "亚马逊销售",
  "navigation.amazonInventory": "亚马逊库存",
  "navigation.amazonPromotion": "亚马逊促销",
  "navigation.operating": "周期经营",
  "navigation.amazonRebateSettlement": "亚马逊返利与结算",
  "navigation.amazonOperation": "亚马逊运营",
  "navigation.amazonDataUpload": "亚马逊数据上传",
  "navigation.joybuySales": "京东国际销售",
  "navigation.joybuyInventory": "京东国际库存",
  "navigation.joybuyOrderPlanning": "京东国际订单计划",
  "navigation.joybuyOperation": "京东国际运营",
  "navigation.joybuyTraffic": "京东国际流量",
  "navigation.joybuyDataUpload": "京东国际数据上传中心",

  // 全球成本 — HQ Model EXW 变动
  "cost.context": "全球 · 权威月度数据依据",
  "cost.title": "成本",
  "cost.description": "HQ Model EXW 变动与月度成本依据。库存成本保持为独立权威口径。",
  "cost.hqModel": "总部型号",
  "cost.exwMovement": "总部型号 EXW 变动",
  "cost.exwHistory": "EXW 成本历史",
  "cost.latestMonth": "最新月份",
  "cost.currency": "币种",
  "cost.unit": "单位",
  "cost.memberCoverage": "成员覆盖",
  "cost.mixedMemberExw": "成员 EXW 不一致",
  "cost.multipleScopes": "存在多个币种/单位口径",
  "cost.hqAuthorityDescription": "仅使用已审批的全球成本精确族组成员关系；浏览器不执行字符串或模糊归组。",
  "cost.noHqAuthority": "无已审批总部型号权威关系",
  "cost.noHqAuthorityDescription": "结果保持未解析，不推断型号族。",
  "cost.latestPeriod": "最新期间",
  "cost.previousPeriod": "上一期间",
  "cost.latestExw": "最新 EXW",
  "cost.previousExw": "上一期 EXW",
  "cost.absoluteChange": "绝对变动",
  "cost.percentageChange": "变动百分比",
  "cost.largestIncrease": "最大涨幅",
  "cost.largestDecrease": "最大降幅",
  "cost.memberCount": "商业型号数",
  "cost.exceptionCount": "异常类型数",
  "cost.familyComparison": "最新可比族内区间",
  "cost.familyComparisonDescription": "最小值、最大值及绝对价差仅由后端在同月份、同币种、同单位内计算。",
  "cost.membershipEvidence": "总部型号成员关系依据",
  "cost.exwEvidence": "EXW 变动数据依据",
  "cost.exwMissing": "EXW 缺失",
  "cost.exwMissingDescription": "缺失不会转为零，也不会进入排名。",
  "cost.inventoryHistory": "库存成本历史",
  "cost.inventorySeparate": "库存成本独立展示，不与 EXW 叠加，也不替代 EXW。",
  "cost.hqExpansionTitle": "总部型号族扩展确认",
  "cost.hqExpansionScope": "核对精确保留成员",
  "cost.hqExpansionDescription": "逐个明确选择已审批 Master Model；不会通过前缀、相似度、模糊匹配或前端推荐建立成员关系。",
  "cost.hqExpansionEmpty": "无已审批总部型号族",
  "cost.hqEffectiveDate": "生效日期",
  "cost.hqRetainedModels": "保留的 Master Model",
  "cost.hqSelectExactModels": "搜索并明确选择 Master Model",
  "cost.hqAdded": "待新增型号",
  "cost.hqRemoved": "待移除型号",
  "cost.hqFactsChanged": "原始成本事实变更",
  "cost.hqReviewConfirmation": "复核确认内容",
  "cost.hqReadOnlySelection": "可进行选择预览；正式确认需要受控 UAT 写入边界和审计操作人。",
  "cost.hqConfirmTitle": "确认总部型号族成员",
  "cost.hqConfirmAction": "确认精确成员",
  "cost.hqConfirmWarning": "带生效日期的权威关系变更",
  "cost.hqConfirmDescription": "仅变更已审批的总部型号成员关系。历史成本事实保持不可变，移除关系保留历史，后端会重新校验每个精确 Master Model。",

  // ------------------------------------------------------------------
  // status
  // ------------------------------------------------------------------
  "status.complete": "完整",
  "status.partial": "部分",
  "status.missing": "缺失",
  "status.unresolved": "未解析",
  "status.derived": "推导值",
  "status.approximate": "近似",
  "status.stale": "过期",
  "status.blocked": "受阻",
  "status.reconciled": "已核对",
  "status.available": "可用",
  "status.matched": "已匹配",
  "status.exact": "精确",
  "status.healthy": "健康",
  "status.calculated": "已计算",
  "status.low": "偏低",
  "status.high": "偏高",
  "status.excess": "过剩",
  "status.critical": "严重",
  "status.source": "原始",
  "status.zero": "零",

  "status.targetUnavailable": "目标不可用",
  "status.revenueMissing": "收入缺失",
  "status.revenueCoveragePartial": "收入覆盖部分",
  "status.customerFactsComplete": "客户数据完整",
  "status.outsideStandardCustomerAggregate": "不在标准客户汇总内",
  "status.noScopedExceptions": "范围内无异常",
  "status.noScopedInvoiceFacts": "范围内无发票数据",
  "status.standardCustomerScope": "标准客户范围",
  "status.freeOfCharge": "免费赠送",
  "status.realZero": "真实零",
  "status.realZeroFactPresent": "存在真实零值数据",
  "status.standardSalesFacts": "标准销售数据",
  "status.signedSalesAdjustmentPresent": "存在签核销售调整",
  "status.approximateFx": "近似 FX",
  "status.fxRequired": "需要 FX",
  "status.revenueUnavailable": "收入不可用",
  "status.resolved": "已解析",
  "status.reviewRequired": "需要复核",
  "status.mtdPartial": "MTD / 部分",
  "status.closedPeriod": "已关闭期间",
  "status.mtdPartialPeriod": "MTD / 部分期间",
  "status.future": "未来",
  "status.closed": "已关闭",
  "status.notApplicable": "不适用",
  "status.noConfirmedSourceExceptions": "当前上下文无已确认的来源异常",
  "status.noConfirmedSourceException": "所选上下文无已确认的来源异常",
  "status.weeklyAuthoritative": "周度权威数据",
  "status.weeklySourceUnavailable": "周度来源不可用",
  "status.monthlySourcePending": "月度来源待定",
  "status.finalAuthoritative": "最终 / 权威数据",
  "status.monthlySourceUnavailable": "月度来源不可用",
  "status.backendComparisonAvailable": "后端对比可用",
  "status.priorPeriodZero": "上一期间为零",
  "status.priorPeriodMissing": "上一期间不可用",
  "status.currentPeriodMissing": "当前期间不可用",

  // ------------------------------------------------------------------
  // metrics (core commercial BI terminology)
  // ------------------------------------------------------------------
  "metrics.sellOut": "二销",
  "metrics.sellOutUnits": "二销数量",
  "metrics.sellIn": "一销",
  "metrics.invoicedSellIn": "开票一销",
  "metrics.invoicedSellInUnits": "开票一销数量",
  "metrics.customerOnHand": "客户在库",
  "metrics.inTransit": "在途库存",
  "metrics.conversion": "转化率",
  "metrics.traffic": "流量",
  "metrics.invoicedUnits": "开票数量",
  "metrics.invoicedRevenue": "开票收入",
  "metrics.invoiceDocuments": "发票单据",
  "metrics.invoiceLines": "发票行",
  "metrics.revenue": "收入",
  "metrics.woc": "WOC（库存周数）",
  "metrics.marketplaceInventory": "平台库存",
  "metrics.panEuStock": "泛欧库存",
  "metrics.currentPo": "当前订单",
  "metrics.openPo": "未完成订单",
  "metrics.poIntake": "新增订单",
  "metrics.invoiceCompletion": "已开票完成量",
  "metrics.netChannelFlow": "渠道净流量",
  "metrics.alignment3m": "3个月一销二销匹配度",
  "metrics.invoicedAmount": "开票金额",
  "metrics.unresolvedTransit": "未解析在途",
  "metrics.actualUnits": "实际数量",
  "metrics.targetUnits": "目标数量",
  "metrics.unitsCompletion": "数量完成度",
  "metrics.actualRevenue": "实际收入",
  "metrics.targetRevenue": "目标收入",
  "metrics.revenueCompletion": "收入完成度",
  "metrics.actualRevenueCoverage": "实际收入覆盖",
  "metrics.targetRevenueCoverage": "目标收入覆盖",
  "metrics.reportingFx": "报表 FX",
  "metrics.customerScope": "客户范围",
  "metrics.trafficUv7d": "流量 · UV 7天",
  "metrics.sellOut7d": "二销 · 7天",
  "metrics.conversion7d": "转化率 · 7天",
  "metrics.sourceTraffic": "原始流量",
  "metrics.trafficStatus": "流量状态",
  "metrics.conversionOrdersTraffic": "转化率 · 订单 / 流量",
  "metrics.conversionStatus": "转化率状态",
  "metrics.sourceConversionRatio": "来源转化率",
  "metrics.sellOutStatus": "二销状态",
  "metrics.mapping": "映射",
  "metrics.source": "来源",
  "metrics.weekComparison": "周度对比",
  "metrics.monthOverMonth": "环比上月",
  "metrics.yearOverYear": "同比去年",

  // ------------------------------------------------------------------
  // authority
  // ------------------------------------------------------------------
  "authority.joybuyOfficialPdfInvoice": "规范销售发票权威",
  "authority.amazonWeeklySales": "周度销售",
  "authority.amazonMonthlySales": "月度销售",
  "authority.amazonMtdWeeklySales": "MTD · 周度销售",
  "authority.activeInvoiceLines": "有效发票行",
  "authority.customerSalesTarget": "客户销售目标",
  "authority.backendCustomerAttainment": "后端客户达成率",
  "authority.invoiceRevenueEurNormalized": "发票收入 · EUR 归一化",
  "authority.customerRevenueTarget": "客户收入目标",
  "authority.businessResolvedInvoiceLines": "业务已解析发票行",
  "authority.approximatePlnEurReportingFx": "近似 PLN→EUR 报表 FX",
  "authority.standardCustomerKpiScope": "标准客户 KPI 范围",
  "authority.activeInvoiceLinesDivTarget": "有效发票行 ÷ 客户销售目标",
  "authority.normalizedInvoiceRevenueDivTarget": "归一化发票收入 ÷ 客户收入目标",
  "authority.currentAcceptedAmazonSourceVersions": "当前已接受的亚马逊来源版本",
  "authority.weeklyOperation": "周度运营",
  "authority.acceptedAmazonStockPoCompletion": "已接受的亚马逊库存 + 精确 PO 完成量",
  "authority.amazonPurchaseOrders": "亚马逊采购订单",
  "authority.activeAmazonInvoices": "有效亚马逊发票",
  "authority.backendInvoicedSellInMinusSellOut": "后端:开票一销 − 二销",
  "authority.currentPoMinusSignedInvoiceCompletion": "当前订单 − 精确签核发票完成量",
  "authority.backendRolling3ClosedMonths": "后端 · 滚动 3 个已关闭月份",
  "authority.activeInvoiceHeadersSummary": "有效发票表头 + 亚马逊发票汇总",
  "authority.backendExactComparableScope": "后端 · 精确可比范围",
  "authority.officialPdfInvoices": "规范销售发票权威",
  "authority.joybuyOperationalSellOut": "京东国际运营二销",
  "authority.sourceRevenue": "{{authority}} · 来源收入",
  "authority.periodComparison": "后端期间对比",

  // ------------------------------------------------------------------
  // shared BI component chrome
  // ------------------------------------------------------------------
  "bi.authority": "权威来源",
  "bi.coverage": "数据覆盖",
  "bi.target": "目标",
  "bi.completion": "完成度",
  "bi.variance": "差异",
  "bi.asOf": "截至 {{value}}",
  "bi.period": "期间 {{value}}",
  "bi.exceptions": "{{count}} 个异常",
  "bi.evidenceSuffix": "数据依据",
  "bi.businessContext": "业务上下文",
  "bi.metricAria": "{{label}} 指标",
  "bi.backendComparison": "后端对比",
  "bi.backendDelta": "后端变化量",
  "bi.netFlow": "净流量",
  "bi.rowCoverage": "行覆盖",
  "bi.productCoverage": "产品覆盖",
  "bi.sourceSkuCoverage": "源 SKU 覆盖",
  "bi.salesInCoverage": "一销覆盖",
  "bi.salesOutCoverage": "二销覆盖",
  "bi.comparableScope": "可比范围",
  "bi.productUnresolved": "{{count}} 个产品未解析",
  "bi.rowsResolved": "{{count}} / {{count2}} 行已解析",
  "bi.rowsResolvedShort": "{{count}} / {{count2}} 行",
  "bi.coverageText": "数据覆盖",
  "bi.feedbackLoading": "加载中",
  "bi.feedbackLoadingDescription": "正在准备最新权威信息。",
  "bi.feedbackNoResults": "无结果",
  "bi.feedbackNoResultsDescription": "当前视图没有可用信息。",
  "bi.feedbackUnavailable": "不可用",
  "bi.feedbackUnavailableDescription": "后端未提供此信息。",
  "bi.feedbackError": "无法加载此视图",
  "bi.feedbackErrorDescription": "请重试只读请求。",
  "bi.feedbackTechnicalDetail": "技术详情",
  "bi.feedbackPartialCoverage": "部分覆盖",
  "bi.feedbackPartialCoverageDescription": "部分后端数据仍然缺失或未解析。",
  "bi.refreshRefreshing": "正在刷新…",
  "bi.refreshUpdated": "刚刚更新",
  "bi.refreshNoop": "已是最新",
  "bi.refreshPartial": "已更新，数据覆盖不完整",
  "bi.refreshFailed": "刷新失败",
  "bi.missingGlyph": "—",

  // ------------------------------------------------------------------
  // charts
  // ------------------------------------------------------------------
  "chart.units": "数量",
  "chart.weeks": "周数",
  "chart.eur": "EUR",
  "chart.visitors": "访客数",
  "chart.rate": "比率",
  "chart.sellOutUnits": "二销数量",
  "chart.invoicedUnits": "开票数量",
  "chart.invoicedRevenueEur": "开票收入 EUR",
  "chart.customerOnHand": "客户在库",
  "chart.inTransit": "在途库存",
  "chart.woc": "WOC",
  "chart.actual": "实际",
  "chart.target": "目标",
  "chart.marketplaceInventory": "平台库存",
  "chart.panEuStock": "泛欧库存",
  "chart.poIntake": "新增订单",
  "chart.invoicedSellIn": "开票一销",
  "chart.sellOut": "二销",
  "chart.trafficUv7d": "流量 · UV 7天",
  "chart.sellOut7d": "二销 · 7天",
  "chart.conversionOrdersTraffic": "转化率 · 订单 / 流量",
  "chart.country": "国家",
  "chart.model": "型号",

  // ------------------------------------------------------------------
  // global sales page
  // ------------------------------------------------------------------
  "pages.globalSales.breadcrumb": "全球 · 商业 BI",
  "pages.globalSales.title": "全球销售表现",
  "pages.globalSales.description": "财务销售及客户目标以发票为实际权威，RN 运营 Sell-in 单独展示。",
  "pages.globalSales.ariaPeriod": "全球销售期间",
  "pages.globalSales.ariaCustomer": "全球销售客户",
  "pages.globalSales.ariaCountry": "全球销售国家",
  "pages.globalSales.ariaModel": "全球销售型号",
  "pages.globalSales.ariaSort": "客户目标排序",
  "pages.globalSales.contextAria": "全球销售业务上下文",
  "pages.globalSales.targetGrainUnavailableTitle": "当前筛选下目标粒度不可用",
  "pages.globalSales.targetGrainUnavailableDescription": "按国家/型号筛选后的实际数据仍然权威。客户期间目标与完成度保持缺失,且不进行分摊。",
  "pages.globalSales.outOfScopeTitle": "客户不在标准客户完成度范围内",
  "pages.globalSales.outOfScopeDescription": "其来源与目标数据依据在下方仍然可用;汇总完成度保持缺失。",
  "pages.globalSales.unitsPerformance": "数量表现",
  "pages.globalSales.unitsPerformanceDescription": "先看完成度,再看同范围的发票实际销售与客户目标。",
  "pages.globalSales.completionVsTime": "完成率与时间进度",
  "pages.globalSales.completionVsTimeDescription": "对比销售完成率与已过去的日历时间进度。进度差是基准,不是预测。",
  "pages.globalSales.timeProgress": "时间进度",
  "pages.globalSales.paceGap": "进度差",
  "pages.globalSales.revenuePerformance": "收入表现",
  "pages.globalSales.revenuePerformanceDescription": "EUR 报表值,含独立的目标与 FX 覆盖。",
  "pages.globalSales.actualVsTargetTrend": "实际 vs 目标趋势",
  "pages.globalSales.actualVsTargetTrendDescription": "仅启用当前后端权威来源支持的序列。",
  "pages.globalSales.unitsTrend": "数量趋势",
  "pages.globalSales.unitsTrendDetail": "实际序列以客户期间粒度存在。实际 vs 目标趋势仍可在所选客户数据依据抽屉中查看;不展示顶层目标序列。",
  "pages.globalSales.revenueTrend": "收入趋势",
  "pages.globalSales.revenueTrendUnavailable": "目标趋势不可用",
  "pages.globalSales.revenueTrendDetail": "不存在顶层归一化的实际/目标收入序列。年度目标不会按期间拆分。",
  "pages.globalSales.customerDriverAnalysis": "客户驱动分析",
  "pages.globalSales.customerDriverAnalysisDescription": "客户期间是权威目标粒度。默认排序使用最大的后端剩余缺口。",
  "pages.globalSales.sortLargestGap": "剩余缺口最大",
  "pages.globalSales.sortLowestAttainment": "完成度最低",
  "pages.globalSales.sortLargestSales": "实际数量最大",
  "pages.globalSales.sortHighestAttainment": "完成度最高",
  "pages.globalSales.countryModelContribution": "国家与型号实际贡献",
  "pages.globalSales.countryModelContributionDescription": "这些维度仅支持权威的筛选后实际数据。当前目标粒度下目标分摊与完成度不可用。",
  "pages.globalSales.noTargetAllocation": "无国家/型号目标分摊",
  "pages.globalSales.noTargetAllocationDescription": "选择筛选条件以查看发票实际销售。客户目标绝不会按实际占比在 React 中分摊。",
  "pages.globalSales.dataQualityScope": "数据质量与范围",
  "pages.globalSales.dataQualityScopeDescription": "数据覆盖、FX 与范围保持可见,不与主要业务结果竞争。",
  "pages.globalSales.exceptions": "销售数据质量与异常",
  "pages.globalSales.exceptionsDescription": "仅确定性当前事实;正式归属与工作流推迟至 BI-006。",
  "pages.globalSales.salesEvidence": "销售数据依据",
  "pages.globalSales.salesEvidenceDescription": "RN 汇总不加载发票覆盖数字；发票仅保留在受管对账流程中，绝不替代 RN 达成事实。",
  "pages.globalSales.noPlnEvidence": "所选范围内无 PLN 数据依据",
  "pages.globalSales.loading": "正在加载全球销售",
  "pages.globalSales.loadingDescription": "正在读取以 RN 为权威的运营 Sell-in、受管客户目标及以发票为权威的财务证据。",
  "pages.globalSales.unavailable": "全球销售不可用",
  "pages.globalSales.loadingCustomerComparison": "正在加载客户对比",
  "pages.globalSales.loadingCustomerTrend": "正在加载客户趋势",
  "pages.globalSales.chartAriaCustomerTarget": "按所选业务字段排序的客户实际与目标对比",
  "pages.globalSales.colCustomer": "客户",
  "pages.globalSales.colCountry": "国家",
  "pages.globalSales.colActualUnits": "RN 数量",
  "pages.globalSales.colTargetUnits": "目标数量",
  "pages.globalSales.colUnitsVariance": "数量差异",
  "pages.globalSales.colUnitsCompletion": "数量完成度",
  "pages.globalSales.colActualRevenue": "实际收入",
  "pages.globalSales.colTargetRevenue": "目标收入",
  "pages.globalSales.colRevenueCompletion": "收入完成度",
  "pages.globalSales.colCoverageScope": "覆盖 / 范围",
  "pages.globalSales.colEvidence": "数据依据",
  "pages.globalSales.viewEvidence": "查看数据依据",
  "pages.globalSales.drawerTitle": "{{customer}} · 销售数据依据",
  "pages.globalSales.drawerFallbackTitle": "客户详情",
  "pages.globalSales.drawerTrendAria": "{{customer}} 实际与目标期间趋势",
  "pages.globalSales.countryMarketActual": "国家 / 市场实际贡献",
  "pages.globalSales.colSourceIdentities": "来源身份",
  "pages.globalSales.colInvoiceLines": "发票行",
  "pages.globalSales.invoiceSourceIdentities": "发票来源身份",
  "pages.globalSales.colSourceCode": "来源代码",
  "pages.globalSales.colSourceLegalCustomer": "来源 / 法人客户",
  "pages.globalSales.colDocumentsLines": "单据 / 行数",
  "pages.globalSales.colInvoiceEvidence": "发票数据依据",
  "pages.globalSales.targetComponents": "目标构成",
  "pages.globalSales.monthValues": "{{count}} 个月度值",
  "pages.globalSales.targetUnavailable": "目标不可用",
  "pages.globalSales.fxRate": "1 PLN = {{rate}} EUR",
  "pages.globalSales.fxRateUnavailable": "汇率不可用",
  "pages.globalSales.approximatePlnEur": "近似 PLN→EUR",
  "pages.globalSales.fxUnavailable": "FX 不可用",
  "pages.globalSales.standardCustomers": "标准客户",
  "pages.globalSales.allCustomers": "全部客户",
  "pages.globalSales.selectedCustomer": "所选客户",
  "pages.globalSales.allCountries": "全部国家",
  "pages.globalSales.allModels": "全部型号",
  "pages.globalSales.selectedModel": "所选型号",
  "pages.globalSales.grainYear": "年",
  "pages.globalSales.grainQuarter": "季度",
  "pages.globalSales.grainMonth": "月",
  "pages.globalSales.selectedCountryActual": "所选国家实际数量",
  "pages.globalSales.selectedModelActual": "所选型号实际数量",
  "pages.globalSales.countryActualHint": "目标粒度不可用;请选择国家以查看实际贡献。",
  "pages.globalSales.modelActualHint": "目标粒度不可用;请选择型号以查看实际贡献。",
  "pages.globalSales.qualityDetailFreeOfCharge": "{{count}} 免费赠送 · {{missing}} 缺失",
  "pages.globalSales.qualityDetailMissingTarget": "{{count}} 行目标缺失",
  "pages.globalSales.qualityDetailFx": "{{rate}} · {{count}} 行 PLN",
  "pages.globalSales.qualityDetailScope": "{{count}} 个已排除 · 数据依据已保留",
  "pages.globalSales.exceptionApproxFx": "近似报表 FX",
  "pages.globalSales.exceptionApproxFxDetail": "{{count}} 行 PLN 使用配置的报表汇率。",
  "pages.globalSales.exceptionExcludedCustomers": "不在汇总完成度范围内的客户",
  "pages.globalSales.exceptionExcludedCustomersDetail": "{{count}} 个客户仍可进行数据依据复核。",
  "pages.globalSales.exceptionTargetGrain": "目标粒度不可用",
  "pages.globalSales.exceptionTargetGrainDetail": "按国家/型号筛选后的实际数据是权威的;目标与完成度保持缺失。",
  "pages.globalSales.exceptionRevenueMissing": "收入事实缺失",
  "pages.globalSales.exceptionRevenueMissingDetail": "{{count}} 行发票仍然缺失。",
  "pages.globalSales.exceptionTargetRevenueMissing": "目标收入缺失",
  "pages.globalSales.exceptionTargetRevenueMissingDetail": "{{count}} 行目标仍然缺失。",
  "pages.globalSales.evidenceViewCustomerTarget": "查看客户目标数据依据",
  "pages.globalSales.evidenceViewRevenue": "查看收入数据依据",
  "pages.globalSales.customerFilterLabel": "客户",
  "pages.globalSales.countryFilterLabel": "国家",
  "pages.globalSales.modelFilterLabel": "型号",
  "pages.globalSales.periodFilterLabel": "期间",
  "pages.globalSales.customerScopeLabel": "范围",
  "pages.globalSales.salesOutcomesAria": "全球销售业务结果",
  "pages.globalSales.colInvoiceNumber": "发票",
  "pages.globalSales.colInvoiceDate": "发票日期",
  "pages.globalSales.colUnits": "数量",
  "pages.globalSales.colRevenuePln": "收入 PLN",
  "pages.globalSales.colEurReporting": "EUR 报表",
  "pages.globalSales.colFxStatus": "FX 状态",
  "authority.backendCalendarReference": "后端日历参考日期",
  "authority.backendCompletionMinusTime": "后端完成率 − 时间进度",

  // ------------------------------------------------------------------
  // amazon sales page
  // ------------------------------------------------------------------
  "pages.amazonSales.viewTitle": "亚马逊销售视图",
  "pages.amazonSales.viewDescription": "二销表现与渠道流转保持独立的报表状态。",
  "pages.amazonSales.sellOutPerformance": "二销表现",
  "pages.amazonSales.channelFlow": "渠道流转",
  "pages.amazonSales.ariaView": "亚马逊销售视图",
  "pages.amazonSales.salesOutcomesAria": "亚马逊销售结果",
  "pages.amazonSales.breadcrumb": "亚马逊 · 商业 BI",
  "pages.amazonSales.title": "亚马逊销售表现",
  "pages.amazonSales.description": "来自单一来源权威的二销结果、期间上下文、国家与精确型号 / ASIN 驱动。",
  "pages.amazonSales.ariaYear": "亚马逊销售年度",
  "pages.amazonSales.ariaGrain": "亚马逊销售期间粒度",
  "pages.amazonSales.ariaPeriod": "亚马逊销售期间",
  "pages.amazonSales.ariaCountry": "亚马逊销售国家",
  "pages.amazonSales.ariaModel": "亚马逊销售型号",
  "pages.amazonSales.ariaDriverSort": "亚马逊销售驱动排序",
  "pages.amazonSales.reportingWeek": "报表周",
  "pages.amazonSales.contextAria": "亚马逊销售业务上下文",
  "pages.amazonSales.mtdAlertTitle": "MTD · 周度销售",
  "pages.amazonSales.mtdAlertDescription": "月度来源待定。KPI、趋势点、国家与型号驱动均使用同一个后端选定的周度 MTD 权威。",
  "pages.amazonSales.salesOutcomes": "销售结果",
  "pages.amazonSales.salesOutcomesDescription": "结果优先展示。亚马逊销售不会人为生成销售目标或完成度。",
  "pages.amazonSales.periodTrend": "期间趋势",
  "pages.amazonSales.periodTrendWeekDescription": "所选周限定 KPI 与驱动;趋势保留每一个权威报表周。",
  "pages.amazonSales.periodTrendMonthDescription": "月度权威构成月、季度与年度趋势点。周度与月度来源绝不混用。",
  "pages.amazonSales.loadingTrend": "正在加载亚马逊销售趋势",
  "pages.amazonSales.trendAria": "亚马逊二销权威期间趋势",
  "pages.amazonSales.colReportingPeriod": "报表期间",
  "pages.amazonSales.colSellOutUnits": "二销数量",
  "pages.amazonSales.colRevenue": "收入",
  "pages.amazonSales.colSource": "来源",
  "pages.amazonSales.colCoverage": "数据覆盖",
  "pages.amazonSales.countryDrivers": "国家驱动",
  "pages.amazonSales.countryDriversDescription": "所选期间的后端事实。零、带符号值与缺失保持区分。",
  "pages.amazonSales.loadingCountryDrivers": "正在加载国家驱动",
  "pages.amazonSales.countryDriversAria": "亚马逊二销国家驱动",
  "pages.amazonSales.colCountry": "国家",
  "pages.amazonSales.colUnits": "数量",
  "pages.amazonSales.colFactStatus": "事实状态",
  "pages.amazonSales.colSourceCoverage": "来源覆盖",
  "pages.amazonSales.colEvidence": "数据依据",
  "pages.amazonSales.sourceRows": "来源行",
  "pages.amazonSales.modelDrivers": "型号 / ASIN 驱动",
  "pages.amazonSales.modelDriversDescription": "精确的后端主型号身份,保留 ASIN 数据依据;不做模糊或首选候选映射。",
  "pages.amazonSales.loadingModelDrivers": "正在加载型号 / ASIN 驱动",
  "pages.amazonSales.modelDriversAria": "亚马逊二销型号与 ASIN 驱动",
  "pages.amazonSales.colModel": "型号",
  "pages.amazonSales.colAsin": "ASIN",
  "pages.amazonSales.sortHighestUnits": "数量最高",
  "pages.amazonSales.sortHighestRevenue": "收入最高",
  "pages.amazonSales.sortIdentity": "身份",
  "pages.amazonSales.sourceAuthorityQuality": "来源权威与数据质量",
  "pages.amazonSales.sourceAuthorityQualityDescription": "紧凑的来源质量元数据保持从属于业务结果。",
  "pages.amazonSales.exceptions": "亚马逊销售异常",
  "pages.amazonSales.exceptionsDescription": "仅已确认的来源条件;带符号调整是保留事实,不会被自动归类为错误。",
  "pages.amazonSales.evidence": "数据依据",
  "pages.amazonSales.evidenceDescription": "KPI、国家与型号事实解析到所选报表期间与当前已接受的来源版本。",
  "pages.amazonSales.sourceUploadHistory": "来源上传历史",
  "pages.amazonSales.identityEvidence": "身份数据依据",
  "pages.amazonSales.batchIdentitiesHint": "批次标识保留在受管上传历史中,而非主 BI 页面。",
  "pages.amazonSales.loading": "正在加载亚马逊销售",
  "pages.amazonSales.loadingDescription": "正在读取所选报表上下文的单一权威销售来源。",
  "pages.amazonSales.unavailable": "亚马逊销售不可用",
  "pages.amazonSales.contextChannel": "渠道",
  "pages.amazonSales.contextGrain": "期间粒度",
  "pages.amazonSales.contextPeriod": "所选期间",
  "pages.amazonSales.contextWeek": "报表周",
  "pages.amazonSales.contextCountry": "国家",
  "pages.amazonSales.contextModel": "型号",
  "pages.amazonSales.contextAuthority": "来源权威",
  "pages.amazonSales.contextSourceStatus": "来源状态",
  "pages.amazonSales.contextCoverage": "来源覆盖",
  "pages.amazonSales.weekEnding": "截至 {{period}} 的周",
  "pages.amazonSales.startUnavailable": "开始日期不可用",
  "pages.amazonSales.coverageRowsResolved": "{{count}} / {{count2}} 行已解析",
  "pages.amazonSales.sourceStatusMetric": "来源状态",
  "pages.amazonSales.qualityProductResolution": "产品解析",
  "pages.amazonSales.qualityProductResolutionDetail": "{{count}} 行来源未解析",
  "pages.amazonSales.qualitySourceBatches": "来源批次",
  "pages.amazonSales.qualitySourceBatchesDetail": "{{count}} 行已接受的当前来源行",
  "pages.amazonSales.qualitySignedFacts": "签核事实",
  "pages.amazonSales.qualitySignedFactsDetail": "合法签核调整保持签核状态。",
  "pages.amazonSales.qualityRealZeroFacts": "真实零值事实",
  "pages.amazonSales.qualityRealZeroFactsDetail": "权威零值与缺失保持区分。",
  "pages.amazonSales.exceptionMonthlyPending": "月度来源待定",
  "pages.amazonSales.exceptionMonthlyPendingDetail": "当月结果仅使用周度销售 MTD。",
  "pages.amazonSales.exceptionSourceMissing": "权威来源不可用",
  "pages.amazonSales.exceptionSourceMissingDetail": "缺失保持缺失;不引入替代的已关闭期间来源。",
  "pages.amazonSales.exceptionUnresolvedProduct": "型号 / ASIN 身份未解析",
  "pages.amazonSales.exceptionUnresolvedProductDetail": "{{count}} 行来源仍未解析。",
  "pages.amazonSales.exceptionSignedAdjustment": "存在签核销售调整",
  "pages.amazonSales.exceptionSignedAdjustmentDetail": "{{count}} 行来源包含权威签核调整。",
  "pages.amazonSales.weekSourceQuality": "完整报表周保持不变,包括跨月周。",
  "pages.amazonSales.mtdQuality": "仅周度推导的 MTD;不加入月度销售。",
  "pages.amazonSales.monthlyFinalQuality": "月度销售完全取代周度 MTD。",
  "pages.amazonSales.monthlyMissingQuality": "缺失保持缺失;已关闭月份不使用周度回退。",
  "pages.amazonSales.trendSourceWeekly": "周度销售",
  "pages.amazonSales.trendSourceMtd": "MTD · 周度推导",
  "pages.amazonSales.trendSourceMonthly": "月度销售",
  "pages.amazonSales.noticeMtdTitle": "MTD · 周度销售",
  "pages.amazonSales.noticeMtdDescription": "来源:周度销售 · 月度来源待定。报表周按其权威周末日期归属。",
  "pages.amazonSales.noticeMonthlyTitle": "来源:月度销售",
  "pages.amazonSales.noticeMonthlyDescription": "已选择权威月度销售;周度销售被排除。",
  "pages.amazonSales.noticeMonthlyUnavailableTitle": "月度销售不可用",
  "pages.amazonSales.noticeMonthlyUnavailableDescription": "缺失保持缺失。已关闭的历史月份不应用周度回退。",
  "pages.amazonSales.evidenceViewSource": "查看来源数据依据",
  "pages.amazonSales.evidenceViewRevenue": "查看收入数据依据",
  "pages.amazonSales.grainWeek": "周",

  // ------------------------------------------------------------------
  // amazon channel flow
  // ------------------------------------------------------------------
  "pages.amazonChannelFlow.breadcrumb": "亚马逊 · 商业 BI",
  "pages.amazonChannelFlow.title": "亚马逊渠道流转",
  "pages.amazonChannelFlow.description": "新增订单 → 开票一销 → 二销,未完成订单保留为时点位置。",
  "pages.amazonChannelFlow.ariaYear": "渠道流转年度",
  "pages.amazonChannelFlow.ariaGrain": "渠道流转期间粒度",
  "pages.amazonChannelFlow.ariaPeriod": "渠道流转期间",
  "pages.amazonChannelFlow.ariaCountry": "渠道流转国家",
  "pages.amazonChannelFlow.ariaModel": "渠道流转型号",
  "pages.amazonChannelFlow.ariaCountrySort": "渠道流转国家排序",
  "pages.amazonChannelFlow.ariaModelSort": "渠道流转型号排序",
  "pages.amazonChannelFlow.contextAria": "亚马逊渠道流转业务上下文",
  "pages.amazonChannelFlow.mtdAlertTitle": "当前月份 · MTD / 部分期间",
  "pages.amazonChannelFlow.mtdAlertDescription": "新增订单使用已接受的 PO 日期,开票一销使用发票日期,二销沿用现有周度 MTD / 月度最终权威。3 个月匹配排除此部分月份。",
  "pages.amazonChannelFlow.flowOutcomes": "流转结果",
  "pages.amazonChannelFlow.flowOutcomesDescription": "主要结果对比实际开票一销与二销;新增订单与未完成订单保留各自不同的流转与位置含义。",
  "pages.amazonChannelFlow.primaryOutcomes": "主要结果",
  "pages.amazonChannelFlow.supportingFlow": "支撑流转",
  "pages.amazonChannelFlow.asOfPosition": "时点位置",
  "pages.amazonChannelFlow.invoiceValueEvidence": "发票金额数据依据",
  "pages.amazonChannelFlow.flowTrend": "流转趋势",
  "pages.amazonChannelFlow.flowTrendDescription": "同一数量轴上的后端日历期间。缺失形成缺口,真实零保持为零,带符号事实保持带符号。",
  "pages.amazonChannelFlow.loadingTrend": "正在加载渠道流转趋势",
  "pages.amazonChannelFlow.trendAria": "亚马逊渠道流转权威趋势",
  "pages.amazonChannelFlow.colPeriod": "期间",
  "pages.amazonChannelFlow.colStatus": "状态",
  "pages.amazonChannelFlow.colPoIntake": "新增订单",
  "pages.amazonChannelFlow.colInvoicedSellIn": "开票一销",
  "pages.amazonChannelFlow.colInvoicedAmount": "开票金额",
  "pages.amazonChannelFlow.colSellOut": "二销",
  "pages.amazonChannelFlow.colNetChannelFlow": "渠道净流量",
  "pages.amazonChannelFlow.colOpenPo": "未完成订单",
  "pages.amazonChannelFlow.colCoverage": "数据覆盖",
  "pages.amazonChannelFlow.colEvidence": "数据依据",
  "pages.amazonChannelFlow.poRows": "{{count}} 行 PO",
  "pages.amazonChannelFlow.countryDrivers": "国家驱动",
  "pages.amazonChannelFlow.countryDriversDescription": "按精确国家上下文的日历期间数量与当前未完成订单位置。",
  "pages.amazonChannelFlow.modelDrivers": "型号驱动",
  "pages.amazonChannelFlow.modelDriversDescription": "国家 + 精确主型号对比。ASIN 保留为数据依据,不是强制性的跨来源关联键。",
  "pages.amazonChannelFlow.colAsinEvidence": "ASIN 数据依据",
  "pages.amazonChannelFlow.sortHighestInvoicedSellIn": "开票一销最高",
  "pages.amazonChannelFlow.sortHighestSellOut": "二销最高",
  "pages.amazonChannelFlow.sortHighestNetFlow": "净流量最高",
  "pages.amazonChannelFlow.sortHighestOpenPo": "未完成订单最高",
  "pages.amazonChannelFlow.alignmentCoverage": "匹配与覆盖",
  "pages.amazonChannelFlow.alignmentCoverageDescription": "每个权威来源均报告后端拥有的行与身份覆盖;React 不推导百分比。",
  "pages.amazonChannelFlow.poSourceCoverage": "PO 来源覆盖",
  "pages.amazonChannelFlow.invoiceContextCoverage": "发票上下文覆盖",
  "pages.amazonChannelFlow.sellOutCoverage": "二销覆盖",
  "pages.amazonChannelFlow.coverageDetail": "{{product}} 个产品 · {{customer}} 个国家/客户 · {{value}} 个值未解析",
  "pages.amazonChannelFlow.dataQualityExceptions": "数据质量 / 异常",
  "pages.amazonChannelFlow.dataQualityExceptionsDescription": "仅列出确定性的来源与映射条件;正或负的渠道净流量不会自动归类为风险。",
  "pages.amazonChannelFlow.confirmed": "{{count}} 个已确认",
  "pages.amazonChannelFlow.evidence": "数据依据",
  "pages.amazonChannelFlow.evidenceDescription": "流转 KPI → 国家 / 型号贡献 → 受管 PO、发票与二销来源数据依据。",
  "pages.amazonChannelFlow.evidencePo": "新增订单 / 未完成订单",
  "pages.amazonChannelFlow.evidenceInvoice": "开票一销",
  "pages.amazonChannelFlow.evidenceInvoiceSummary": "发票汇总金额",
  "pages.amazonChannelFlow.evidenceSellOut": "二销批次",
  "pages.amazonChannelFlow.evidenceIdentity": "身份数据依据",
  "pages.amazonChannelFlow.loading": "正在加载亚马逊渠道流转",
  "pages.amazonChannelFlow.loadingDescription": "正在按日历粒度对齐 PO、有效发票与二销权威。",
  "pages.amazonChannelFlow.unavailable": "亚马逊渠道流转不可用",
  "pages.amazonChannelFlow.loadingCountryDrivers": "正在加载渠道流转国家驱动",
  "pages.amazonChannelFlow.loadingModelDrivers": "正在加载渠道流转型号驱动",
  "pages.amazonChannelFlow.qualityFlowProxy": "仅为流转代理;非实际库存。",
  "pages.amazonChannelFlow.qualityPoFlow": "PO 流转;非已完成的一销。",
  "pages.amazonChannelFlow.qualityInvoiceValueOnly": "仅为发票金额;非二销收入,不参与数量匹配。",
  "pages.amazonChannelFlow.alignmentDenominatorZero": "不适用:开票一销分母为零。",
  "pages.amazonChannelFlow.alignmentDenominatorNegative": "受阻:签核分母为负。",
  "pages.amazonChannelFlow.alignmentSourceCoverageIncomplete": "受阻:三个月来源覆盖不完整。",
  "pages.amazonChannelFlow.alignmentThreeMonthsNotAvailable": "三个已关闭月份不可用。",
  "pages.amazonChannelFlow.exceptionPoWithoutInvoice": "PO 无发票完成量",
  "pages.amazonChannelFlow.exceptionInvoiceWithoutPo": "发票无 PO 引用",
  "pages.amazonChannelFlow.exceptionInvoiceModelUnresolved": "发票型号未解析",
  "pages.amazonChannelFlow.exceptionPoModelUnresolved": "PO 型号未解析",
  "pages.amazonChannelFlow.exceptionCountryContextMismatch": "国家上下文不匹配",
  "pages.amazonChannelFlow.exceptionSellOutModelUnresolved": "二销型号未解析",
  "pages.amazonChannelFlow.exceptionSourceCoverageIncomplete": "来源覆盖不完整",
  "pages.amazonChannelFlow.exceptionCurrentMonthPartial": "当前月份 · MTD / 部分期间",
  "pages.amazonChannelFlow.exceptionConfirmedSource": "已确认来源异常",
  "pages.amazonChannelFlow.contextView": "渠道流转",
  "pages.amazonChannelFlow.contextPoAuthority": "PO 权威",
  "pages.amazonChannelFlow.contextInvoiceAuthority": "开票一销权威",
  "pages.amazonChannelFlow.contextSellOutAuthority": "二销权威",
  "pages.amazonChannelFlow.contextAsOf": "截至日期",
  "pages.amazonChannelFlow.contextCountry": "国家",
  "pages.amazonChannelFlow.colCountry": "国家",
  "pages.amazonChannelFlow.colModel": "型号",
  "pages.amazonChannelFlow.poEvidence": "PO 数据依据",
  "pages.amazonChannelFlow.invoiceEvidence": "发票数据依据",
  "pages.amazonChannelFlow.invoiceSummaryEvidence": "发票汇总数据依据",
  "pages.amazonChannelFlow.sellOutEvidence": "二销数据依据",
  "pages.amazonChannelFlow.sellInUnitsMetric": "开票一销数量",
  "pages.amazonChannelFlow.sellOutUnitsMetric": "二销数量",
  "pages.amazonChannelFlow.netChannelFlowMetric": "渠道净流量",
  "pages.amazonChannelFlow.poIntakeUnitsMetric": "新增订单数量",
  "pages.amazonChannelFlow.openPoUnitsMetric": "未完成订单数量",
  "pages.amazonChannelFlow.invoicedAmountMetric": "开票金额",
  "pages.amazonChannelFlow.periodLabelMtd": "{{period}} · MTD / 部分期间",
  "pages.amazonChannelFlow.periodLabelClosed": "{{period}} · 已关闭期间",
  "pages.amazonChannelFlow.asOfPositionHint": "时点位置 · {{date}}",

  // ------------------------------------------------------------------
  // amazon inventory page
  // ------------------------------------------------------------------
  "pages.amazonInventory.tabCommercialBi": "商业 BI",
  "pages.amazonInventory.tabCpfrPlanning": "CPFR 计划",
  "pages.amazonInventory.breadcrumb": "亚马逊 · 商业 BI",
  "pages.amazonInventory.title": "亚马逊库存",
  "pages.amazonInventory.description": "按精确型号 / ASIN 展示亚马逊运营库存、原生周粒度二销 WOC 与供应位置。",
  "pages.amazonInventory.ariaCountry": "亚马逊库存国家",
  "pages.amazonInventory.ariaModel": "亚马逊库存型号",
  "pages.amazonInventory.ariaAsin": "亚马逊库存 ASIN",
  "pages.amazonInventory.ariaSnapshot": "亚马逊库存快照",
  "pages.amazonInventory.ariaSort": "亚马逊库存排序",
  "pages.amazonInventory.scopeAllAcceptedCountries": "全部已接受国家",
  "pages.amazonInventory.contextAria": "亚马逊库存业务上下文",
  "pages.amazonInventory.partialAlertTitle": "库存覆盖为部分",
  "pages.amazonInventory.partialAlertDescription": "缺失的四周二销覆盖或 ASIN 粒度 PO 数据依据保持缺失。不会从其他产品、市场或报告周推断任何值。",
  "pages.amazonInventory.stockOutcomes": "按范围的亚马逊库存",
  "pages.amazonInventory.stockOutcomesDescription": "欧盟亚马逊库存与所选国家亚马逊库存是同一已接受库存权威的两个范围，不作为独立库存池相加。WOC 及其所有组成值均由后端提供。",
  "pages.amazonInventory.asOf": "截至日期",
  "pages.amazonInventory.selectedReportingWeeks": "所选报告周",
  "pages.amazonInventory.weekCoverage": "报告周覆盖",
  "pages.amazonInventory.supplyPosition": "供应位置",
  "pages.amazonInventory.supplyPositionDescription": "截至 {{date}}:当年 PO 减去精确签核发票完成量。在途库存使用同一冻结的未完成订单权威。",
  "pages.amazonInventory.viewChannelFlow": "查看渠道流转",
  "pages.amazonInventory.inventoryTrend": "库存趋势",
  "pages.amazonInventory.inventoryTrendDescription": "仅绘制已接受的后端快照历史;WOC 趋势保持不可用,因为不存在带版本的聚合 WOC 序列。",
  "pages.amazonInventory.loadingTrend": "正在加载库存趋势",
  "pages.amazonInventory.trendAria": "亚马逊已接受库存快照趋势",
  "pages.amazonInventory.trendNotAvailable": "库存趋势不可用",
  "pages.amazonInventory.trendNotAvailableDescription": "该范围的已接受来源仅包含一个快照。",
  "pages.amazonInventory.countryDrivers": "国家驱动",
  "pages.amazonInventory.countryDriversDescription": "国家是后端筛选条件。可选择全部已接受国家，或 DE、FR、IT、ES、UK 等实际可用国家；后端提供的更广库存位置仍保留为独立列。",
  "pages.amazonInventory.modelAsinDrivers": "型号 / ASIN 驱动",
  "pages.amazonInventory.modelAsinDriversDescription": "精确国家 + 主型号 ID + ASIN 来源身份。缺失、零与带符号值保持区分。",
  "pages.amazonInventory.sortInventory": "库存",
  "pages.amazonInventory.sortPanEu": "泛欧",
  "pages.amazonInventory.sortWoc": "WOC",
  "pages.amazonInventory.sortOpenPo": "未完成订单",
  "pages.amazonInventory.sortInTransit": "在途库存",
  "pages.amazonInventory.noRowsTitle": "无权威库存行",
  "pages.amazonInventory.noRowsDescription": "所选范围不存在精确行。",
  "pages.amazonInventory.dataQualityExceptions": "数据质量与异常",
  "pages.amazonInventory.dataQualityExceptionsDescription": "仅确定性后端状态;无前端阈值或推断映射。",
  "pages.amazonInventory.marketplaceInventoryCoverage": "所选国家库存覆盖",
  "pages.amazonInventory.coverageDetail": "{{count}} 行 WOC 已覆盖 · {{count2}} 行 PO 已覆盖",
  "pages.amazonInventory.evidence": "数据依据",
  "pages.amazonInventory.evidenceDescription": "业务标签优先;来源批次与行级身份保留在数据依据路径之后。",
  "pages.amazonInventory.authorityEvidence": "权威与来源数据依据",
  "pages.amazonInventory.inventorySource": "库存来源",
  "pages.amazonInventory.sellOutSource": "二销来源",
  "pages.amazonInventory.poInvoiceReconciliation": "PO / 发票核对",
  "pages.amazonInventory.identityEvidence": "身份数据依据",
  "pages.amazonInventory.lineageTitle": "精确来源血缘 · {{count}} 行",
  "pages.amazonInventory.colSourceFile": "来源文件",
  "pages.amazonInventory.colPoEvidence": "PO 数据依据",
  "pages.amazonInventory.loading": "正在加载亚马逊库存",
  "pages.amazonInventory.loadingDescription": "正在读取已接受的库存、WOC 与精确 PO 完成量权威。",
  "pages.amazonInventory.unavailable": "亚马逊库存不可用",
  "pages.amazonInventory.colMarketplaceInventory": "平台库存",
  "pages.amazonInventory.colPanEu": "泛欧",
  "pages.amazonInventory.colCountryAmazonStock": "国家亚马逊库存",
  "pages.amazonInventory.colEuAmazonStock": "欧盟亚马逊库存",
  "pages.amazonInventory.colTrailing4wSellOut": "过去4周二销",
  "pages.amazonInventory.colAverageWeeklySellOut": "平均周销",
  "pages.amazonInventory.colWoc": "WOC",
  "pages.amazonInventory.colWocStatus": "WOC 状态",
  "pages.amazonInventory.colOpenPo": "未完成订单",
  "pages.amazonInventory.colInvoiceCompletion": "已开票完成量",
  "pages.amazonInventory.colInTransit": "在途库存",
  "pages.amazonInventory.colRisk": "风险",
  "pages.amazonInventory.colCoverage": "数据覆盖",
  "pages.amazonInventory.colEvidence": "数据依据",
  "pages.amazonInventory.poEvidenceCell": "{{count}} PO · {{source}}",
  "pages.amazonInventory.contextSupplyAsOf": "供应截至",
  "pages.amazonInventory.contextAuthority": "权威",
  "pages.amazonInventory.authorityValue": "已接受的亚马逊库存 + 精确 PO 完成量",
  "pages.amazonInventory.metricMarketplaceInventory": "平台库存",
  "pages.amazonInventory.metricPanEuStock": "泛欧库存",
  "pages.amazonInventory.metricCountryAmazonStock": "所选国家亚马逊库存",
  "pages.amazonInventory.metricEuAmazonStock": "欧盟亚马逊库存",
  "pages.amazonInventory.metricTrailing4wSellOut": "过去4周二销",
  "pages.amazonInventory.metricAverageWeeklySellOut": "平均周销",
  "pages.amazonInventory.metricWoc": "WOC（库存周数）",
  "pages.amazonInventory.sparseZeroTitle": "WOC 已审批稀疏周零值规则",
  "pages.amazonInventory.sparseZeroNote": "在完整且权威的报告周内，如某库存商品没有二销记录，WOC 计算按该周二销 0 处理。整周缺失及未解析身份仍保持缺失。",
  "pages.amazonInventory.wocCoverageEvidence": "WOC 覆盖依据",
  "pages.amazonInventory.wocCoverageCounts": "{{rows}} 条库存行 · {{source}} 个来源身份周 · {{sparse}} 个稀疏零身份周 · {{unresolved}} 个未解析身份周",
  "pages.amazonInventory.metricCurrentPo": "当前订单",
  "pages.amazonInventory.metricInvoiceCompletion": "已开票完成量",
  "pages.amazonInventory.metricOpenPo": "未完成订单",
  "pages.amazonInventory.metricInTransit": "在途库存",
  "pages.amazonInventory.marketplaceBadge": "平台:{{status}}",
  "pages.amazonInventory.panEuBadge": "泛欧:{{status}}",
  "pages.amazonInventory.wocBadge": "WOC:{{status}}",

  // ------------------------------------------------------------------
  // joybuy sales page
  // ------------------------------------------------------------------
  "pages.joybuySales.viewTitle": "京东国际销售视图",
  "pages.joybuySales.viewDescription": "一销二销匹配与发票实际销售保持独立的报表状态。",
  "pages.joybuySales.salesAlignment": "一销二销匹配",
  "pages.joybuySales.invoiceActual": "发票实际销售",
  "pages.joybuySales.ariaView": "京东国际销售视图",
  "pages.joybuySales.breadcrumb": "京东国际 · 商业 BI · 开票一销",
  "pages.joybuySales.title": "京东国际开票销售",
  "pages.joybuySales.description": "按开票日期、国家与精确型号展示规范发票结果；SAP 与 PDF 证据仅按已接受的来源权威及去重规则纳入。二销、流量与转化率保留在京东国际运营中。",
  "pages.joybuySales.ariaYear": "京东国际销售年度",
  "pages.joybuySales.ariaGrain": "京东国际销售粒度",
  "pages.joybuySales.ariaPeriod": "京东国际销售期间",
  "pages.joybuySales.ariaCountry": "京东国际销售国家",
  "pages.joybuySales.ariaModel": "京东国际销售型号",
  "pages.joybuySales.ariaFocOnly": "京东国际销售仅看免费赠送",
  "pages.joybuySales.ariaUnresolvedOnly": "京东国际销售仅看未解析",
  "pages.joybuySales.focOnly": "仅看免费赠送",
  "pages.joybuySales.contextAria": "京东国际销售业务上下文",
  "pages.joybuySales.sellInOnlyTitle": "仅开票一销",
  "pages.joybuySales.sellInOnlyDescription": "SAP 与 PDF 证据仅通过已接受的来源权威及精确去重规则贡献。下游二销、流量与转化率请{{link}}。",
  "pages.joybuySales.viewJoybuyOperation": "查看京东国际运营",
  "pages.joybuySales.invoicedOutcomes": "开票销售结果",
  "pages.joybuySales.invoicedOutcomesDescription": "零是已覆盖的业务事实;缺失保持缺失。仅当报表权威解析该行时收入才为 EUR。",
  "pages.joybuySales.periodTrend": "期间趋势",
  "pages.joybuySales.periodTrendDescription": "按权威发票开票日期的后端日历汇总。空期间(包括缺失的 4 月)保持缺失而非零。",
  "pages.joybuySales.loadingTrend": "正在加载开票销售趋势",
  "pages.joybuySales.trendAria": "京东国际开票数量与收入趋势",
  "pages.joybuySales.countryDrivers": "国家驱动",
  "pages.joybuySales.countryDriversDescription": "仅显示规范京东国际发票事实中出现的国家；运营地理范围不会扩大此范围。",
  "pages.joybuySales.loadingCountryDrivers": "正在加载国家驱动",
  "pages.joybuySales.countryDriversAria": "京东国际开票销售国家驱动",
  "pages.joybuySales.noCountryFacts": "无国家数据",
  "pages.joybuySales.noCountryFactsDescription": "该期间与筛选条件下不存在权威发票行。",
  "pages.joybuySales.modelDrivers": "型号驱动",
  "pages.joybuySales.modelDriversDescription": "精确国家 + 主型号 ID 粒度。未解析身份保持可见且可筛选。",
  "pages.joybuySales.noModelFacts": "无型号数据",
  "pages.joybuySales.noModelFactsDescription": "该期间与筛选条件下不存在权威型号行。",
  "pages.joybuySales.coverageQuality": "发票覆盖与数据质量",
  "pages.joybuySales.coverageQualityDescription": "最新发票开票日期:{{date}}。有效与已被取代的数据依据保持区分。",
  "pages.joybuySales.revenueCoverage": "收入覆盖",
  "pages.joybuySales.productResolution": "产品解析",
  "pages.joybuySales.countryCoverage": "国家覆盖",
  "pages.joybuySales.coverageDetailFoc": "{{count}} 行显式免费赠送 · {{missing}} 行缺失",
  "pages.joybuySales.unresolvedCount": "{{count}} 个未解析",
  "pages.joybuySales.yearEvidence": "年度数据依据:{{activeDocs}} 份有效单据 / {{activeLines}} 行有效行 · {{supersededDocs}} 份已取代单据 / {{supersededLines}} 行已取代行 · {{replayRuns}} 次重放 · 所选范围内 {{signed}} 行签核行。",
  "pages.joybuySales.exceptions": "异常",
  "pages.joybuySales.exceptionsDescription": "仅确定性的缺失或未解析权威。显式免费赠送与签核法律事实是分类,不是自动错误。",
  "pages.joybuySales.evidence": "数据依据",
  "pages.joybuySales.evidenceDescription": "发票单据、有效行、原始 PDF 金额与业务已解析收入保持可追溯。",
  "pages.joybuySales.authorityBoundaries": "权威与来源边界",
  "pages.joybuySales.bulkReconciliationContribution": "汇总文件核对贡献:{{value}}",
  "pages.joybuySales.invoiceUploadHistory": "发票上传历史",
  "pages.joybuySales.identityEvidence": "身份数据依据",
  "pages.joybuySales.invoiceDocuments": "发票单据 · {{count}}",
  "pages.joybuySales.loading": "正在加载京东国际开票销售",
  "pages.joybuySales.loadingDescription": "正在读取受管的规范发票与发票行权威。",
  "pages.joybuySales.unavailable": "京东国际销售不可用",
  "pages.joybuySales.colInvoice": "发票",
  "pages.joybuySales.colIssueDate": "开票日期",
  "pages.joybuySales.colCustomer": "客户",
  "pages.joybuySales.colCountry": "国家",
  "pages.joybuySales.colUnits": "数量",
  "pages.joybuySales.colRevenueEur": "收入 EUR",
  "pages.joybuySales.colLines": "行数",
  "pages.joybuySales.colFoc": "免费赠送",
  "pages.joybuySales.colMissingRevenue": "缺失收入",
  "pages.joybuySales.colSourcePdf": "来源 PDF",
  "pages.joybuySales.colLine": "行",
  "pages.joybuySales.colPdfPageRow": "PDF 页 / 行",
  "pages.joybuySales.colSku": "SKU",
  "pages.joybuySales.colModel": "型号",
  "pages.joybuySales.colQuantity": "数量",
  "pages.joybuySales.colRawPdfAmount": "原始 PDF 金额",
  "pages.joybuySales.colReportingEur": "报表 EUR",
  "pages.joybuySales.colRevenueStatus": "收入状态",
  "pages.joybuySales.colInvoicedUnits": "开票数量",
  "pages.joybuySales.colRevenueEurDriver": "收入 EUR",
  "pages.joybuySales.colInvoices": "发票数",
  "pages.joybuySales.colRevenueCoverage": "收入覆盖",
  "pages.joybuySales.contextPeriod": "开票日期期间",
  "pages.joybuySales.contextPeriodState": "期间状态",
  "pages.joybuySales.contextCountry": "国家",
  "pages.joybuySales.contextModel": "型号",
  "pages.joybuySales.contextAuthority": "来源权威",
  "pages.joybuySales.metricInvoicedUnits": "开票数量",
  "pages.joybuySales.metricInvoicedRevenue": "开票收入",
  "pages.joybuySales.metricInvoiceDocuments": "发票单据",
  "pages.joybuySales.metricInvoiceLines": "发票行",
  "pages.joybuySales.evidenceInvoice": "发票数据依据",

  // ------------------------------------------------------------------
  // joybuy sales alignment
  // ------------------------------------------------------------------
  "pages.joybuyAlignment.breadcrumb": "京东国际 · 商业 BI",
  "pages.joybuyAlignment.title": "京东国际一销二销匹配",
  "pages.joybuyAlignment.description": "在精确国家 + 型号范围内，对比规范开票一销与京东国际运营二销。",
  "pages.joybuyAlignment.ariaYear": "京东国际匹配年度",
  "pages.joybuyAlignment.ariaGrain": "京东国际匹配粒度",
  "pages.joybuyAlignment.ariaPeriod": "京东国际匹配期间",
  "pages.joybuyAlignment.ariaCountry": "京东国际匹配国家",
  "pages.joybuyAlignment.ariaModel": "京东国际匹配型号",
  "pages.joybuyAlignment.ariaCountrySort": "京东国际匹配国家排序",
  "pages.joybuyAlignment.ariaModelSort": "京东国际匹配型号排序",
  "pages.joybuyAlignment.contextAria": "京东国际一销二销匹配业务上下文",
  "pages.joybuyAlignment.mtdAlertTitle": "当前月份 · MTD / 部分期间",
  "pages.joybuyAlignment.mtdAlertDescription": "当前月份保持部分状态,并排除在滚动 3 个已关闭月份匹配之外。",
  "pages.joybuyAlignment.comparableScopeTitle": "仅可比范围",
  "pages.joybuyAlignment.comparableScopeDescription": "渠道净流量与 3 个月匹配使用精确国家 + 主型号匹配。缺失权威绝不变为零。",
  "pages.joybuyAlignment.alignmentOutcomes": "匹配结果",
  "pages.joybuyAlignment.alignmentOutcomesDescription": "渠道净流量是可比范围内开票一销减去二销;它是流转代理,而非实际库存。",
  "pages.joybuyAlignment.primaryOutcomes": "主要结果",
  "pages.joybuyAlignment.supporting": "支撑",
  "pages.joybuyAlignment.sellInSellOutTrend": "一销 vs 二销趋势",
  "pages.joybuyAlignment.sellInSellOutTrendDescription": "后端日历汇总。缺失形成缺口,真实零保持为零,带符号值保持带符号。",
  "pages.joybuyAlignment.loadingTrend": "正在加载匹配趋势",
  "pages.joybuyAlignment.trendAria": "京东国际一销与二销趋势",
  "pages.joybuyAlignment.colPeriod": "期间",
  "pages.joybuyAlignment.colStatus": "状态",
  "pages.joybuyAlignment.colInvoicedSellIn": "开票一销",
  "pages.joybuyAlignment.colSellOut": "二销",
  "pages.joybuyAlignment.colNetChannelFlow": "渠道净流量",
  "pages.joybuyAlignment.countryDrivers": "国家驱动",
  "pages.joybuyAlignment.countryDriversDescription": "缺少任一精确权威的国家保持不可比;其缺失侧显示为缺失。",
  "pages.joybuyAlignment.modelDrivers": "型号驱动",
  "pages.joybuyAlignment.modelDriversDescription": "对比键为精确国家 + 主型号 ID。ASIN 不用作关联键。",
  "pages.joybuyAlignment.col3mAlignment": "3 个月匹配",
  "pages.joybuyAlignment.colComparable": "可比",
  "pages.joybuyAlignment.colCoverage": "数据覆盖",
  "pages.joybuyAlignment.colEvidence": "数据依据",
  "pages.joybuyAlignment.coverageCell": "{{sellIn}} · {{sellOut}}",
  "pages.joybuyAlignment.evidenceCell": "{{count}} 一销 · {{count2}} 二销",
  "pages.joybuyAlignment.sortHighestSellIn": "一销最高",
  "pages.joybuyAlignment.sortHighestSellOut": "二销最高",
  "pages.joybuyAlignment.sortHighestNetFlow": "净流量最高",
  "pages.joybuyAlignment.sortHighestAlignment": "匹配度最高",
  "pages.joybuyAlignment.coverageQuality": "覆盖与数据质量",
  "pages.joybuyAlignment.coverageQualityDescription": "覆盖与可比范围由后端拥有。",
  "pages.joybuyAlignment.exactPairs": "{{count}} 对精确匹配",
  "pages.joybuyAlignment.nonComparable": "{{countries}} 个不可比国家",
  "pages.joybuyAlignment.noNonComparable": "无不可比国家",
  "pages.joybuyAlignment.exceptions": "异常",
  "pages.joybuyAlignment.exceptionsDescription": "仅确定性权威、范围、映射与部分期间事实。",
  "pages.joybuyAlignment.evidence": "数据依据",
  "pages.joybuyAlignment.evidenceDescription": "一销与二销保留独立血缘;匹配连接两个权威。",
  "pages.joybuyAlignment.sellInInvoiceEvidence": "一销发票数据依据",
  "pages.joybuyAlignment.sellOutOperationEvidence": "二销运营数据依据",
  "pages.joybuyAlignment.identityEvidence": "身份数据依据",
  "pages.joybuyAlignment.viewTrafficConversion": "查看流量与转化率",
  "pages.joybuyAlignment.viewInventory": "查看库存",
  "pages.joybuyAlignment.loading": "正在加载京东国际一销二销匹配",
  "pages.joybuyAlignment.loadingDescription": "正在将规范开票一销与受管运营二销对齐。",
  "pages.joybuyAlignment.unavailable": "京东国际一销二销匹配不可用",
  "pages.joybuyAlignment.metricInvoicedSellInUnits": "开票一销数量",
  "pages.joybuyAlignment.metricSellOutUnits": "二销数量",
  "pages.joybuyAlignment.metricNetChannelFlow": "渠道净流量",
  "pages.joybuyAlignment.metric3mSellThrough": "3个月一销二销匹配度",
  "chart.netChannelFlow": "渠道净流量",
  "status.unavailable": "不可用",
  "pages.joybuyAlignment.colCountry": "国家",
  "pages.joybuyAlignment.colModel": "型号",
  "pages.joybuyAlignment.colSellInMapping": "一销映射",
  "pages.joybuyAlignment.colSellOutMapping": "二销映射",

  "pages.joybuyAlignment.qualityFlowProxy": "仅为流转代理;非实际库存。",
  "pages.joybuyAlignment.contextView": "一销二销匹配",
  "pages.joybuyAlignment.contextSellInAuthority": "一销权威",
  "pages.joybuyAlignment.contextSellOutAuthority": "二销权威",
  "pages.joybuyAlignment.contextComparableScope": "可比范围",
  "pages.joybuyAlignment.contextAsOf": "截至日期",
  "pages.joybuyAlignment.unavailableScope": "不可用",
  "pages.joybuyAlignment.evidenceAlignment": "匹配数据依据",

  // ------------------------------------------------------------------
  // joybuy inventory page
  // ------------------------------------------------------------------
  "pages.joybuyInventory.breadcrumb": "京东国际 · 商业 BI",
  "pages.joybuyInventory.title": "京东国际库存",
  "pages.joybuyInventory.description": "来自已接受周度运营权威的当前库存、在途位置与精确源 SKU 数据依据。",
  "pages.joybuyInventory.ariaCountry": "京东国际库存国家",
  "pages.joybuyInventory.ariaModel": "京东国际库存型号",
  "pages.joybuyInventory.ariaSourceSku": "京东国际库存源 SKU",
  "pages.joybuyInventory.ariaSnapshot": "京东国际库存快照",
  "pages.joybuyInventory.ariaWocState": "京东国际库存 WOC 状态",
  "pages.joybuyInventory.ariaSort": "京东国际库存排序",
  "pages.joybuyInventory.contextAria": "京东国际库存业务上下文",
  "pages.joybuyInventory.partialAlertTitle": "库存覆盖为部分",
  "pages.joybuyInventory.partialAlertDescription": "未解析身份与来源缺失值保持显式;React 不会补入历史快照、旧上下文或缺失值。",
  "pages.joybuyInventory.inventoryOutcomes": "库存结果",
  "pages.joybuyInventory.inventoryOutcomesDescription": "库存、位置、支撑与质量具有不同的视觉角色;不存在前端可用总量计算。",
  "pages.joybuyInventory.primaryStock": "主要库存",
  "pages.joybuyInventory.position": "位置",
  "pages.joybuyInventory.supportingQuality": "支撑与质量",
  "pages.joybuyInventory.stockTransitPosition": "库存与在途位置",
  "pages.joybuyInventory.stockTransitPositionDescription": "在途库存是当前时点位置,不是一销/二销流转。后端核对将未解析桶保留在头条。",
  "pages.joybuyInventory.headline": "头条 {{value}}",
  "pages.joybuyInventory.mapped": "已映射 {{value}}",
  "pages.joybuyInventory.unresolvedValue": "未解析 {{value}}",
  "pages.joybuyInventory.trendTitle": "权威库存趋势",
  "pages.joybuyInventory.trendDescription": "每个已接受报表快照一个点,按时间升序。缺口保持缺口,历史代际绝不相加。",
  "pages.joybuyInventory.loadingTrend": "正在加载库存趋势",
  "pages.joybuyInventory.trendAria": "京东国际已接受库存快照趋势",
  "pages.joybuyInventory.noTrendTitle": "该范围内无权威趋势",
  "pages.joybuyInventory.countryDrivers": "国家驱动",
  "pages.joybuyInventory.countryDriversDescription": "来自所选后端快照的精确站点/国家事实;使用表头对在库、在途库存、WOC 或未解析在途排序。",
  "pages.joybuyInventory.modelSkuDrivers": "型号 / SKU 驱动",
  "pages.joybuyInventory.modelSkuDriversDescription": "所选快照下的精确国家 + 主型号 ID,或未解析源 SKU。零、缺失与带符号值保持区分。",
  "pages.joybuyInventory.sortOnHand": "在库",
  "pages.joybuyInventory.sortInTransit": "在途库存",
  "pages.joybuyInventory.sortWoc": "WOC",
  "pages.joybuyInventory.sortUnresolved": "未解析",
  "pages.joybuyInventory.noRowsTitle": "无权威库存行",
  "pages.joybuyInventory.noRowsDescription": "所选后端范围不存在精确行。",
  "pages.joybuyInventory.coverageFreshness": "覆盖与新鲜度",
  "pages.joybuyInventory.coverageFreshnessDescription": "覆盖与新鲜度是后端字段。未发明任何过期阈值。",
  "pages.joybuyInventory.onHandCoverage": "在库覆盖",
  "pages.joybuyInventory.inTransitCoverage": "在途库存覆盖",
  "pages.joybuyInventory.wocCoverage": "WOC 覆盖",
  "pages.joybuyInventory.exactMappingCoverage": "精确映射覆盖",
  "pages.joybuyInventory.mappingCoverageDetail": "{{count}} 行未解析 · {{freshness}}",
  "pages.joybuyInventory.exceptions": "异常",
  "pages.joybuyInventory.exceptionsDescription": "仅确定性来源与覆盖状态;不引入新的库存风险阈值。",
  "pages.joybuyInventory.evidence": "数据依据",
  "pages.joybuyInventory.evidenceDescription": "业务数据依据路径保留快照、映射与来源血缘,不在主视图中暴露原始 DTO。",
  "pages.joybuyInventory.authorityEvidence": "权威与来源数据依据",
  "pages.joybuyInventory.weeklyOperationSource": "周度运营来源",
  "pages.joybuyInventory.identityMapping": "身份映射",
  "pages.joybuyInventory.snapshotLineage": "快照血缘",
  "pages.joybuyInventory.lineageTitle": "精确贡献血缘 · {{count}} 行",
  "pages.joybuyInventory.colState": "状态",
  "pages.joybuyInventory.viewSalesAlignment": "查看一销二销匹配",
  "pages.joybuyInventory.viewTrafficConversion": "查看流量与转化率",
  "pages.joybuyInventory.loading": "正在加载京东国际库存",
  "pages.joybuyInventory.loadingDescription": "正在读取一个已接受的库存快照与紧凑的后端拥有覆盖。",
  "pages.joybuyInventory.unavailable": "京东国际库存不可用",
  "pages.joybuyInventory.colSourceSku": "源 SKU",
  "pages.joybuyInventory.colCountry": "国家",
  "pages.joybuyInventory.colModel": "型号",

  "pages.joybuyInventory.colCustomerOnHand": "客户在库",
  "pages.joybuyInventory.colInTransit": "在途库存",
  "pages.joybuyInventory.colWoc": "WOC",
  "pages.joybuyInventory.colWocStatus": "WOC 状态",
  "pages.joybuyInventory.colMapping": "映射",
  "pages.joybuyInventory.colSnapshot": "库存快照",
  "pages.joybuyInventory.colCoverage": "数据覆盖",
  "pages.joybuyInventory.colEvidence": "数据依据",
  "pages.joybuyInventory.colUnresolvedTransit": "未解析在途",
  "pages.joybuyInventory.colMappedUnresolved": "已映射 / 未解析",
  "pages.joybuyInventory.colSource": "来源",
  "pages.joybuyInventory.colSourceRow": "来源行",
  "pages.joybuyInventory.metricCustomerOnHand": "客户在库",
  "pages.joybuyInventory.metricInTransit": "在途库存",
  "pages.joybuyInventory.metricWoc": "WOC（库存周数）",
  "pages.joybuyInventory.metricUnresolvedTransit": "未解析在途",
  "pages.joybuyInventory.mappedPlusUnresolved": "{{mapped}} 已映射 + {{unresolved}} 未解析",
  "pages.joybuyInventory.contextView": "库存位置",
  "pages.joybuyInventory.contextInventoryAuthority": "库存权威",
  "pages.joybuyInventory.contextTransitAuthority": "在途权威",
  "pages.joybuyInventory.contextWocAuthority": "WOC 权威",
  "pages.joybuyInventory.contextSourceFreshness": "来源新鲜度",
  "pages.joybuyInventory.contextMappingCoverage": "映射覆盖",
  "pages.joybuyInventory.authorityWeeklyOperation": "周度运营",
  "pages.joybuyInventory.trendBadge": "{{label}}:{{value}}",

  // ------------------------------------------------------------------
  // joybuy operation page
  // ------------------------------------------------------------------
  "pages.joybuyOperation.breadcrumb": "京东国际 · 商业 BI",
  "pages.joybuyOperation.title": "京东国际运营",
  "pages.joybuyOperation.description": "型号流量、后端订单 / 流量转化率与权威二销——与库存、WOC、广告和定价保持分离。",
  "pages.joybuyOperation.ariaPeriod": "京东国际运营期间",
  "pages.joybuyOperation.ariaCountry": "京东国际运营国家",
  "pages.joybuyOperation.ariaModel": "京东国际运营型号",
  "pages.joybuyOperation.ariaConversionState": "京东国际运营转化率状态",
  "pages.joybuyOperation.ariaSellOutState": "京东国际运营二销状态",
  "pages.joybuyOperation.ariaSort": "京东国际运营排序",
  "pages.joybuyOperation.contextAria": "京东国际运营业务上下文",
  "pages.joybuyOperation.partialAlertTitle": "运营来源覆盖为部分",
  "pages.joybuyOperation.partialAlertDescription": "缺失的订单或流量与未解析产品身份保持显式。转化率仅使用权威配对的订单 / 流量行;真实零保持已覆盖。",
  "pages.joybuyOperation.operationOutcomes": "运营结果",
  "pages.joybuyOperation.operationOutcomesDescription": "后端拥有的当前期间值。转化率为权威配对行上的订单总和 / 流量总和,当输入或流量分母缺失时保持缺失。",
  "pages.joybuyOperation.trafficSellOutTrend": "流量与二销趋势",
  "pages.joybuyOperation.trafficSellOutTrendDescription": "真实解析器验证的报表日期。流量使用左侧访客轴;二销使用右侧数量轴;缺失保持为缺口。",
  "pages.joybuyOperation.loadingTrafficTrend": "正在加载流量与二销趋势",
  "pages.joybuyOperation.trafficTrendAria": "京东国际流量与二销趋势(独立轴)",
  "pages.joybuyOperation.conversionTrend": "转化率趋势",
  "pages.joybuyOperation.conversionTrendDescription": "活动国家范围内的独立后端订单 / 流量序列;无 React 比率,每个报表期间一个点。",
  "pages.joybuyOperation.loadingConversionTrend": "正在加载转化率趋势",
  "pages.joybuyOperation.conversionTrendAria": "京东国际订单 / 流量转化率趋势",
  "pages.joybuyOperation.conversionMissingTitle": "该范围内转化率缺失",
  "pages.joybuyOperation.conversionMissingDescription": "需要权威配对的订单与型号流量;缺失流量绝不会从来源转化率重建。",
  "pages.joybuyOperation.countryDrivers": "国家驱动",
  "pages.joybuyOperation.countryDriversDescription": "来自后端的精确运营站点上下文;无硬编码国家列表。",
  "pages.joybuyOperation.modelSkuDrivers": "型号 / SKU 驱动",
  "pages.joybuyOperation.modelSkuDriversDescription": "所选期间 + 精确国家 + 源 SKU + 解析时的精确主型号。每个转化率都由后端从该型号行的订单 / 流量计算;来源转化率仅作为数据依据。",
  "pages.joybuyOperation.sortSellOut": "二销",
  "pages.joybuyOperation.sortTraffic": "流量",
  "pages.joybuyOperation.sortConversion": "转化率",
  "pages.joybuyOperation.sortUnresolved": "未解析",
  "pages.joybuyOperation.noRowsTitle": "无权威运营行",
  "pages.joybuyOperation.noRowsDescription": "没有来源行匹配所选后端筛选条件。",
  "pages.joybuyOperation.funnelRelationship": "漏斗与期间关系",
  "pages.joybuyOperation.funnelRelationshipDescription": "后端提供的中性环比对比。它们是描述性变化量,不是因果归因。",
  "pages.joybuyOperation.previousCurrent": "上期 {{previous}} · 本期 {{current}}",
  "pages.joybuyOperation.coverageFreshness": "覆盖与新鲜度",
  "pages.joybuyOperation.coverageFreshnessDescription": "覆盖与新鲜度是后端字段。未批准或发明任何过期阈值。",
  "pages.joybuyOperation.trafficCoverage": "流量覆盖",
  "pages.joybuyOperation.trafficCoverageDetail": "{{source}} 来源 · {{derived}} 已批准回退",
  "pages.joybuyOperation.conversionCoverage": "转化率覆盖",
  "pages.joybuyOperation.sellOutCoverage": "二销覆盖",
  "pages.joybuyOperation.exactMappingCoverage": "精确映射覆盖",
  "pages.joybuyOperation.mappingCoverageDetail": "{{count}} 未解析 · {{freshness}}",
  "pages.joybuyOperation.exceptions": "异常",
  "pages.joybuyOperation.exceptionsDescription": "仅确定性数据质量与来源状态;无低流量、低转化率或因果风险阈值。",
  "pages.joybuyOperation.colState": "状态",
  "pages.joybuyOperation.colAffectedRows": "受影响行数",
  "pages.joybuyOperation.colEvidence": "数据依据",
  "pages.joybuyOperation.colSampleKeys": "示例键",
  "pages.joybuyOperation.evidence": "数据依据",
  "pages.joybuyOperation.evidenceDescription": "权威、期间、身份与来源行血缘保持可审计,不在主视图中暴露批次 UUID。",
  "pages.joybuyOperation.authoritySemanticEvidence": "权威与语义数据依据",
  "pages.joybuyOperation.weeklyOperationSource": "周度运营来源",
  "pages.joybuyOperation.lineageTitle": "精确来源行血缘 · {{count}} 行",
  "pages.joybuyOperation.colSourceRow": "来源行",
  "pages.joybuyOperation.crossLinks": "交叉链接",
  "pages.joybuyOperation.crossLinksDescription": "打开相邻权威,而无需将销售匹配或库存公式复制到运营中。",
  "pages.joybuyOperation.loading": "正在加载京东国际运营",
  "pages.joybuyOperation.loadingDescription": "正在读取紧凑的流量、转化率与二销权威投影。",
  "pages.joybuyOperation.unavailable": "京东国际运营不可用",
  "pages.joybuyOperation.colTrafficUv7d": "流量 · UV 7天",
  "pages.joybuyOperation.colSellOut7d": "二销 · 7天",
  "pages.joybuyOperation.colConversion": "转化率",
  "pages.joybuyOperation.colSourceSkus": "源 SKU 数",
  "pages.joybuyOperation.colUnresolved": "未解析",
  "pages.joybuyOperation.colModel": "型号",
  "pages.joybuyOperation.colPeriod": "期间",

  "pages.joybuyOperation.colSourceSku": "源 SKU",
  "pages.joybuyOperation.colCountry": "国家",
  "pages.joybuyOperation.colSourceTraffic": "原始流量",
  "pages.joybuyOperation.colTrafficStatus": "流量状态",
  "pages.joybuyOperation.colConversionOrdersTraffic": "转化率 · 订单 / 流量",
  "pages.joybuyOperation.colConversionStatus": "转化率状态",
  "pages.joybuyOperation.colSourceConversionRatio": "来源转化率",
  "pages.joybuyOperation.colSellOutStatus": "二销状态",
  "pages.joybuyOperation.colMapping": "映射",
  "pages.joybuyOperation.colSource": "来源",
  "pages.joybuyOperation.metricTraffic": "流量 · UV 7天",
  "pages.joybuyOperation.metricConversion": "转化率 · 7天",
  "pages.joybuyOperation.metricSellOut": "二销 · 7天",
  "pages.joybuyOperation.contextView": "流量 · 转化率 · 二销",
  "pages.joybuyOperation.contextGrain": "粒度",
  "pages.joybuyOperation.contextFreshness": "新鲜度",
  "pages.joybuyOperation.grainValue": "期间 + 国家 + 源 SKU",
  "pages.joybuyOperation.selectedModel": "所选型号",
  "pages.joybuyOperation.allModels": "全部型号",
  "pages.joybuyOperation.allConversionStates": "全部转化率状态",
  "pages.joybuyOperation.allSellOutStates": "全部二销状态",
  "pages.joybuyOperation.allWocStates": "全部 WOC 状态",
  "pages.joybuyOperation.realZero": "真实零",
  "parity.calculateDraft": "计算草稿",
  "parity.saveFreeze": "保存 / 冻结",
  "parity.compareCurrent": "与当前结果对比",
  "parity.selectedFrozenRun": "所选冻结运行",
  "parity.frozenRunPlaceholder": "选择冻结运行",
  "parity.uatActor": "UAT 审计操作者",
  "parity.proposalRunTitle": "Proposal 计算 / 参考运行",
  "parity.proposalContext": "Proposal / 合同上下文",
  "parity.calculateReview": "计算 / 审核",
  "parity.generateReference": "生成参考运行",
  "parity.refreshRuns": "刷新运行",
  "parity.joybuyMixTitle": "型号组合与装载决策",
  "parity.joybuyMixDescription": "每个精确 master_model_id 对应一条型号组合记录。容量、利用率和剩余托盘均来自受治理的装载配置，不按屏幕尺寸推断。",
};
